import React, { useEffect, useState } from "react";
import { HeaderLeftContent2, StyledHeader } from "../Common/styles/header";
import {
  Box,
  Drawer,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  NoDataContainer,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
  pageSize,
} from "../Common/styles/table";
import DataTableLayout from "../Common/UI/DataTableLayout";
import DataTableFooter from "../Common/UI/DataTableFooter";
import {
  dataTableListShell,
  dataTablePageShell,
} from "../../utils/dataTableShell";
import { AxiosResponse } from "axios";
import http from "../../utils/http";
import { errorToastMessage } from "../../utils/toast";
import { useParams, useSearchParams } from "react-router-dom";
import { DateTime } from "luxon";
import {
  formatEventName,
  getAuditEndDate,
  getAuditStartDate,
} from "../../utils/audit";
import AuditDetailsModal from "./AuditDetailsModal";
import AuditTrailFilterDrawer from "./AuditTrailFilterDrawer";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

const AuditTrail = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [auditData, setAuditData] = useState<any>([]);
  const [auditDataCount, setAuditDataCount] = useState(0);
  const { id } = useParams();

  const [showModal, setShowModal] = useState(false);
  const [showFilterModal, setShowFilterDrawer] = useState(false);
  const [modaldetails, setModalDetails] = useState<any>();
  const [startDate, setStartDate] = useState<string | null>(
    searchParams.get("start") || null
  );
  const [endDate, setEndDate] = useState<string | null>(
    searchParams.get("end") || null
  );
  const [eventType, setEventType] = useState<string>(
    searchParams.get("eventType") || ""
  );

  useEffect(() => {
    const params = new URLSearchParams();
    if (startDate) {
      params.set("start", startDate);
    }
    if (endDate) {
      params.set("end", endDate);
    }
    if (eventType) {
      params.set("eventType", eventType);
    }
    setSearchParams(params, {
      replace: true,
    });
  }, [setSearchParams, startDate, endDate, eventType]);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        const start = getAuditStartDate(startDate);
        let end = getAuditEndDate(endDate);
        let url = `/audit-trail?studyId=${id}&page=${
          page + 1
        }&size=${pageSize}`;

        if (start) {
          url += `&createdAtStart=${start.toUTC().toISO()}`;
        }
        if (end) {
          url += `&createdAtEnd=${end.toUTC().toISO()}`;
        }
        if (eventType) {
          url += `&eventType=${eventType}`;
        }

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data;
        const newData = data?.audits?.map((row: any) => ({
          id: row?.id,
          user: row?.actorName,
          userEmail: row?.actorEmail,
          participant: row?.participantId,
          action: formatEventName(row?.eventType),
          date: DateTime.fromISO(row?.createdAt).toFormat(
            "MM/dd/yyyy hh:mm:ss a"
          ),
          details: row?.details,
        }));
        setAuditData(newData || []);
        setAuditDataCount(data?.count || 0);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchDetails();
  }, [startDate, endDate, eventType, page, id]);

  const handleChangePage = (_1: any, newPage: number) => {
    setPage(newPage);
  };

  const showDetails = (row: any) => {
    setModalDetails(row);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalDetails(null);
  };

  const openFilterDrawer = () => {
    setShowFilterDrawer(true);
  };

  const closeFilterDrawer = () => {
    setShowFilterDrawer(false);
  };

  return (
    <>
      <StyledHeader
        sx={{
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Box sx={HeaderLeftContent2}>
          <Typography variant="h2" color="text.primary">
            Audit trail
          </Typography>
        </Box>
      </StyledHeader>
      <Box sx={{ ...dataTablePageShell("calc(100% - 72px)"), p: 2.5 }}>
        <Box
          sx={{
            ...dataTableListShell,
            p: 2,
            backgroundColor: "#fff",
            borderRadius: "10px",
          }}
        >
          <DataTableLayout
            toolbar={
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Box
                  sx={{
                    ml: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <IconButton
                    onClick={openFilterDrawer}
                    sx={{
                      height: "48px",
                      width: "48px",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "8px",
                    }}
                  >
                    <FilterAltIcon
                      color={
                        startDate || endDate || eventType ? "primary" : "disabled"
                      }
                      sx={{ fontSize: "24px" }}
                    />
                  </IconButton>
                </Box>
              </Box>
            }
            footer={
              <DataTableFooter
                filteredCount={auditDataCount}
                page={page}
                pageSize={pageSize}
                onPageChange={handleChangePage}
                loading={loading}
              />
            }
          >
            <Table
              sx={TableBorderRadiusTopLeftRight}
              stickyHeader
              className="scrollable-table"
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell>Date</StyledTableCell>
                  <StyledTableCell>User</StyledTableCell>
                  <StyledTableCell>User Email</StyledTableCell>
                  <StyledTableCell>Participant</StyledTableCell>
                  <StyledTableCell>Action</StyledTableCell>
                </TableRow>
              </TableHead>
              {!loading && auditData?.length > 0 && (
                <TableBody>
                  {auditData?.map((row: any) => (
                    <TableRow
                      key={row?.id}
                      onClick={() => showDetails(row)}
                      sx={{ cursor: "pointer" }}
                    >
                      <StyledTableCell sx={{ maxWidth: "250px" }}>
                        <Typography noWrap variant="body1">
                          {row?.date}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{
                          maxWidth: "250px",
                          color: "primary.main",
                        }}
                      >
                        <Typography
                          noWrap
                          fontWeight={"medium"}
                          variant="body1"
                        >
                          {row?.user}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{
                          maxWidth: "250px",
                          color: "primary.main",
                        }}
                      >
                        <Typography
                          noWrap
                          fontWeight={"medium"}
                          variant="body1"
                        >
                          {row?.userEmail}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell sx={{ maxWidth: "250px" }}>
                        <Typography noWrap variant="body1">
                          {row?.participant}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell sx={{ maxWidth: "250px" }}>
                        <Typography noWrap variant="body1">
                          {row?.action}
                        </Typography>
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {loading && (
              <Box sx={{ width: "100%" }}>
                <LinearProgress />
              </Box>
            )}
            {!loading && auditData.length === 0 && (
              <NoDataContainer>
                <Typography variant="body1" color="gray">
                  No Data
                </Typography>
              </NoDataContainer>
            )}
          </DataTableLayout>
        </Box>
      </Box>
      {showModal && (
        <AuditDetailsModal
          closeModal={closeModal}
          showModal={showModal}
          audit={modaldetails}
        />
      )}

      {showFilterModal && (
        <Drawer
          open={showFilterModal}
          onClose={closeFilterDrawer}
          anchor="right"
        >
          <AuditTrailFilterDrawer
            closeModal={closeFilterDrawer}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            eventType={eventType}
            setEventType={setEventType}
            setPage={setPage}
            type="study_audits"
          />
        </Drawer>
      )}
    </>
  );
};

export default AuditTrail;
